import React, { useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { useAuth } from '../providers/AuthProvider';
import { Wallet, Coins, Shield, Zap, Trophy, ArrowRight, X, Sparkles } from 'lucide-react';
import { Button } from '../components/ui/button';

const LoginScreen = ({ onClose }) => {
  const { connected, connecting, createLocalWallet, user, walletAddress } = useAuth();
  const [isCreating, setIsCreating] = useState(false);

  const features = [
    { icon: Wallet, title: 'Local Wallet', desc: 'No downloads. Instant account creation.' },
    { icon: Shield, title: 'True Ownership', desc: 'Your cards are yours. Trade them freely.' },
    { icon: Coins, title: 'Earn Tokens', desc: 'Win PvP battles to earn Degen Tokens.' },
    { icon: Trophy, title: 'Compete Globally', desc: 'Climb the leaderboard and prove your strength.' },
  ];

  const handleCreateWallet = async () => {
    setIsCreating(true);
    try {
      await createLocalWallet();
      // Success - component will re-render with connected state
    } catch (err) {
      console.error('Failed to create wallet:', err);
    } finally {
      setIsCreating(false);
    }
  };

  // If connected, show success state
  if (connected && user) {
    return (
      <motion.div
        className="fixed inset-0 z-[1000] flex items-center justify-center bg-black/95"
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        exit={{ opacity: 0 }}
      >
        {/* Close button */}
        <button
          onClick={onClose}
          className="absolute top-4 right-4 p-3 bg-red-500/20 hover:bg-red-500/40 rounded-full transition-colors z-10"
          data-testid="login-close-btn"
        >
          <X className="w-6 h-6 text-red-400" />
        </button>

        <motion.div
          className="text-center"
          initial={{ scale: 0.8, opacity: 0 }}
          animate={{ scale: 1, opacity: 1 }}
          transition={{ delay: 0.2 }}
        >
          <motion.div
            className="w-24 h-24 mx-auto mb-6 rounded-full bg-neon-green/20 flex items-center justify-center"
            animate={{ scale: [1, 1.1, 1], boxShadow: ['0 0 20px rgba(0,255,153,0.3)', '0 0 40px rgba(0,255,153,0.6)', '0 0 20px rgba(0,255,153,0.3)'] }}
            transition={{ duration: 1.5, repeat: Infinity }}
          >
            <Zap className="w-12 h-12 text-neon-green" />
          </motion.div>
          
          <h2 className="font-anton text-4xl text-white mb-2">WALLET READY!</h2>
          <p className="font-space text-sm text-white/60 mb-2">Welcome, Degen</p>
          <p className="font-mono text-xs text-neon-green mb-6 px-4 py-2 bg-neon-green/10 rounded-lg inline-block">
            Player ID: {walletAddress || 'Unknown'}
          </p>
          
          <div className="flex items-center justify-center gap-4 mb-8">
            <div className="px-4 py-2 bg-yellow-500/20 rounded-lg">
              <p className="font-space text-xs text-white/60">BALANCE</p>
              <p className="font-anton text-2xl text-yellow-400">{(user.slap_tokens || user.coins || 0).toLocaleString()}</p>
            </div>
            <div className="px-4 py-2 bg-neon-pink/20 rounded-lg">
              <p className="font-space text-xs text-white/60">CARDS</p>
              <p className="font-anton text-2xl text-neon-pink">{user.cards_owned || 0}</p>
            </div>
          </div>
          
          <Button
            onClick={onClose}
            className="bg-neon-green hover:bg-neon-green/80 text-black font-anton text-xl uppercase px-8 py-4"
          >
            ENTER THE ARENA
            <ArrowRight className="w-6 h-6 ml-2" />
          </Button>
        </motion.div>
      </motion.div>
    );
  }

  return (
    <motion.div
      className="fixed inset-0 z-[1000] flex items-center justify-center overflow-hidden"
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      exit={{ opacity: 0 }}
    >
      {/* Cyberpunk background */}
      <div className="absolute inset-0 bg-[#050505]">
        <div 
          className="absolute inset-0 opacity-20"
          style={{
            backgroundImage: `
              linear-gradient(#FF00FF10 1px, transparent 1px),
              linear-gradient(90deg, #00FFFF10 1px, transparent 1px)
            `,
            backgroundSize: '40px 40px',
          }}
        />
        <div className="absolute top-1/4 left-1/4 w-[600px] h-[600px] bg-neon-pink/20 rounded-full blur-[150px]" />
        <div className="absolute bottom-1/4 right-1/4 w-[600px] h-[600px] bg-green-500/20 rounded-full blur-[150px]" />
      </div>

      {/* Close button */}
      <button
        onClick={onClose}
        className="absolute top-4 right-4 p-3 bg-red-500/20 hover:bg-red-500/40 rounded-full transition-colors z-10"
        data-testid="login-close-btn"
      >
        <X className="w-6 h-6 text-red-400" />
      </button>

      <div className="relative z-10 max-w-lg w-full mx-4">
        {/* Logo */}
        <motion.div
          className="text-center mb-8"
          initial={{ y: -50, opacity: 0 }}
          animate={{ y: 0, opacity: 1 }}
        >
          <h1 
            className="font-anton text-5xl md:text-6xl uppercase"
            style={{
              background: 'linear-gradient(180deg, #FFFFFF 0%, #FF00FF 50%, #00FF99 100%)',
              WebkitBackgroundClip: 'text',
              WebkitTextFillColor: 'transparent',
              backgroundClip: 'text',
            }}
          >
            SLAP BATTLES
          </h1>
          <p className="font-space text-sm text-white/60 mt-2">THE MIDNIGHT ARCADE</p>
        </motion.div>

        {/* Main card */}
        <motion.div
          className="bg-black/60 border-2 border-green-500/50 rounded-2xl p-8 backdrop-blur-lg"
          initial={{ scale: 0.9, opacity: 0 }}
          animate={{ scale: 1, opacity: 1 }}
          transition={{ delay: 0.2 }}
        >
          <div className="text-center mb-8">
            <motion.div
              className="w-20 h-20 mx-auto mb-4 rounded-full bg-gradient-to-br from-green-500 to-emerald-600 flex items-center justify-center"
              animate={{ scale: [1, 1.1, 1] }}
              transition={{ duration: 2, repeat: Infinity }}
            >
              <Zap className="w-10 h-10 text-white" />
            </motion.div>
            <h2 className="font-anton text-3xl text-white mb-2">CREATE PLAYER ACCOUNT</h2>
            <p className="font-space text-sm text-white/60">
              No downloads needed. Start playing instantly!
            </p>
          </div>

          {/* Create Wallet Button */}
          <div className="flex flex-col items-center gap-4 mb-8">
            <Button
              onClick={handleCreateWallet}
              disabled={isCreating || connecting}
              className="bg-gradient-to-r from-green-500 to-emerald-500 hover:from-green-400 hover:to-emerald-400 rounded-xl font-anton text-lg py-4 px-8 transition-all shadow-[0_0_30px_rgba(34,197,94,0.5)] hover:shadow-[0_0_50px_rgba(34,197,94,0.7)] w-full"
              data-testid="create-wallet-btn"
            >
              {isCreating || connecting ? (
                <span className="animate-pulse flex items-center gap-2">
                  <Sparkles className="w-6 h-6 animate-spin" />
                  Creating...
                </span>
              ) : (
                <>
                  <Zap className="w-6 h-6 mr-2" />
                  CREATE PLAYER ACCOUNT
                </>
              )}
            </Button>
          </div>

          {/* Features */}
          <motion.div
            className="grid grid-cols-2 gap-4 mt-6"
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
          >
            {features.map((feature, i) => (
              <motion.div
                key={i}
                className="p-3 bg-white/5 rounded-lg border border-white/10"
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: 0.3 + i * 0.1 }}
              >
                <feature.icon className="w-6 h-6 text-green-400 mb-2" />
                <h4 className="font-anton text-xs text-white">{feature.title}</h4>
                <p className="font-space text-[10px] text-white/50">{feature.desc}</p>
              </motion.div>
            ))}
          </motion.div>
        </motion.div>

        {/* Footer */}
        <p className="text-center font-space text-xs text-white/30 mt-6">
          By playing, you agree to the Terms of Service
        </p>
      </div>
    </motion.div>
  );
};

export default LoginScreen;
